import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import {
  KentGetRebalancingConstraintsRequest,
  KentRebalancingConstraints,
} from '@cast/types-kent';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_REBALANCING_CONSTRAINTS_QUERY_KEY = 'kent-rebalancing-constraints';

const getKentRebalancingConstraintsQueryKey = (
  request?: Omit<KentGetRebalancingConstraintsRequest, 'organizationId'>
) => [KENT_REBALANCING_CONSTRAINTS_QUERY_KEY, request] as const;

const getKentRebalancingConstraintsQueryOptions = ({
  organizationId,
  request,
  enabled,
}: {
  organizationId?: string;
  request: Omit<KentGetRebalancingConstraintsRequest, 'organizationId'>;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getKentRebalancingConstraintsQueryKey(request),
    queryFn: async () => {
      const { data } = await apiClient.kent.getRebalancingConstraints({
        organizationId: organizationId ?? '',
        ...request,
      });
      return data;
    },
    enabled: !!organizationId && (enabled ?? true),
  });

type GetRebalancingConstraintsQueryArgs =
  QueryOptions<KentRebalancingConstraints> & {
    request: Omit<KentGetRebalancingConstraintsRequest, 'organizationId'>;
  };

export const useGetKentRebalancingConstraintsQuery = ({
  request,
  enabled,
}: GetRebalancingConstraintsQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getKentRebalancingConstraintsQueryOptions({
      organizationId: currentOrganization?.id,
      request,
      enabled,
    })
  );
};
